import React, { useEffect, useRef, useState } from 'react';
import LazyLoader from './LazyLoader';
import { getTeamMembers } from '../api/api';

const Team = () => {
    const [isFirstSectionVisible, setIsFirstSectionVisible] = useState(false);
    const [isInViewFirst, setIsInViewFirst] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const firstSectionRef = useRef(null);
    const firstElementRef = useRef(null);

    // Fetch team members from API
    useEffect(() => {
        const fetchTeamMembers = async () => {
            try {
                const response = await getTeamMembers();
                if (response.status && response.data) {
                    setTeamMembers(response.data);
                }
            } catch (error) {
                console.error('Error fetching team members:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTeamMembers();
    }, []);

    // Auto-slide every 20 seconds
    useEffect(() => {
        if (teamMembers.length === 0) return;

        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === teamMembers.length - 1 ? 0 : prevIndex + 1
            );
        }, 20000); // 20 seconds

        return () => clearInterval(interval);
    }, [teamMembers.length]);

    useEffect(() => {
        const observerFirst = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setIsFirstSectionVisible(true);
                }
            },
            {
                threshold: 0.5,
            }
        );

        const observerSecond = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    setIsInViewFirst(true);
                }
            },
            {
                threshold: 0.5,
            }
        );

        if (firstSectionRef.current) {
            observerFirst.observe(firstSectionRef.current);
        }

        if (firstElementRef.current) {
            observerSecond.observe(firstElementRef.current);
        }

        return () => {
            if (firstSectionRef.current) {
                observerFirst.unobserve(firstSectionRef.current);
            }
            if (firstElementRef.current) {
                observerSecond.unobserve(firstElementRef.current);
            }
        };
    }, []);

    if (loading) {
        return (
            <div className="flex flex-wrap w-full pb-80 custom-padding xl-screen-padding gap-4 md:gap-16">
                <div className="w-full text-center py-20">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#8f762c] border-r-transparent"></div>
                    <p className="mt-2 text-gray-600">Loading team members...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-wrap w-full pb-80 custom-padding xl-screen-padding gap-4 md:gap-16">
            {/* Left side content - unchanged */}
            <div className={`w-full md:w-7/12 space-left-extra content-container-tab md:pt-0 ${isFirstSectionVisible ? "animate-slide-up" : ""}`}>
                <h2
                    ref={firstElementRef}
                    className={`custom-padding-bottom text-[#2b343b] text-h2 font-semibold text-gray-800 client-text ${isInViewFirst ? 'typewriter-effect' : ''}`}
                >
                    <span className='text-4xl'>
                        <span className="text-black">Our</span> <span className="text-[#8f762c]">Team</span>
                    </span>
                </h2>
                <p className="custom-padding-bottom text-p font-lato text-[#6f787e]">
                    At Nutrillion, what sets us apart is the strength and expertise of our clinical team. We are
                    a diverse group of professionals brought together by a shared passion for science,
                    innovation, and the transformative potential of personalised nutrition.
                </p>
                <p className="custom-padding-bottom text-p font-lato text-[#6f787e]">
                    Our team includes specialists in molecular biology, genetics, and clinical nutritionalists,
                    who work collaboratively to offer insights in the field of nutrigenomics. Together, we
                    blend scientific research with practical applications to design personalised solutions that
                    support individuals to achieve optimal health.
                </p>
                <p className="custom-padding-bottom text-p font-lato text-[#6f787e]">
                    By leveraging our collective expertise, we stay at the forefront of advancements in
                    nutrigenomics and also ensure that every solution we develop is deeply rooted in science
                    and tailored to individual needs. It's this combination of collaboration, and scientific
                    precision that makes Nutrillion a leader in personalised nutrition and a trusted partner on
                    your health journey.
                </p>
            </div>

            {/* Right side - Sliding Square Images */}
            <div className={`w-full md:w-4/12 custom-width relative teamimg ${isFirstSectionVisible ? "animate-slide-up" : ""} mr-6`}>
                <div className="relative col-tablet rounded-lg flex flex-col items-center justify-center md:justify-end">
                    {teamMembers.length > 0 && (
                        <>
                            <div className="relative w-[300px] h-[400px] md:w-[400px] md:h-[500px] shadow-lg overflow-hidden bg-gray-100 rounded-lg">                                <img
                                src={teamMembers[currentImageIndex].imageUrl}
                                alt={teamMembers[currentImageIndex].name}
                                className="object-cover w-full h-full transition-all duration-500 ease-in-out"
                            />

                                {/* Name and Designation Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                                    <h3 className="text-white font-semibold text-lg">
                                        {teamMembers[currentImageIndex].name}
                                    </h3>
                                    <p className="text-gray-200 text-sm mt-1 whitespace-pre-line">
                                        {teamMembers[currentImageIndex].designation}
                                    </p>
                                </div>
                            </div>

                            {/* Navigation Dots - Outside the image, below it */}
                            {teamMembers.length > 1 && (
                                <div className="flex justify-center gap-3 mt-4">
                                    {teamMembers.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`transition-all duration-300 rounded-full ${currentImageIndex === index
                                                ? 'bg-[#8f762c] w-3 h-3'
                                                : 'bg-gray-400 w-2 h-2'
                                                }`}
                                            aria-label={`Go to team member ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Team;